import type { ReactNode } from 'react'
import { useRouter } from '@tanstack/react-router'
import { CodeBlock } from '@/components/Code'
import { MermaidDiagram } from '@/components/Mermaid'
import { inAppPath, linkTarget, splitLinks } from './links'

// Just enough markdown for an agent's replies: fenced code (```mermaid draws a
// diagram), inline code, bold, links, bullet and numbered lists, GFM pipe
// tables and headings shown as bold lines. React elements only, never innerHTML (the one SVG is
// mermaid's own sanitised output, in MermaidDiagram), so nothing an agent
// writes can run in the page.

/** A link in an agent's words: an app path goes through the router, an outside one through /leave. */
const MdLink = ({ href, children }: { href: string; children: ReactNode }) => {
  const router = useRouter()
  const t = linkTarget(href)
  if (!t) return <>{children}</>
  const to = inAppPath(t)
  return (
    <a
      href={to}
      onClick={(e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return
        e.preventDefault()
        router.history.push(to)
      }}
      className="text-accent-text underline underline-offset-2 break-words"
      data-testid={t.kind === 'out' ? 'md-link-out' : 'md-link'}
    >
      {children}
    </a>
  )
}

const inline = (text: string, key: string): ReactNode[] => {
  const out: ReactNode[] = []
  // `code` first (its contents are literal), then **bold**
  const parts = text.split(/(`[^`\n]+`)/g)
  parts.forEach((p, i) => {
    if (p.startsWith('`') && p.endsWith('`') && p.length > 1) {
      out.push(
        <code key={`${key}-${i}`} className="font-mono text-[0.9em] px-1 py-0.5 rounded bg-raised">
          {p.slice(1, -1)}
        </code>,
      )
      return
    }
    p.split(/(\*\*[^*\n]+\*\*)/g).forEach((q, j) => {
      if (q.startsWith('**') && q.endsWith('**') && q.length > 4) {
        out.push(<strong key={`${key}-${i}-${j}`}>{q.slice(2, -2)}</strong>)
        return
      }
      splitLinks(q).forEach((part, k) => {
        if ('text' in part) out.push(part.text)
        else
          out.push(
            <MdLink key={`${key}-${i}-${j}-${k}`} href={part.href}>
              {part.label}
            </MdLink>,
          )
      })
    })
  })
  return out
}

export type TableAlign = 'left' | 'center' | 'right' | null
export type Table = { header: string[]; align: TableAlign[]; rows: string[][] }

/** A row's cells: split on pipes that are neither escaped nor inside `code`, outer pipes dropped. */
export const splitRow = (line: string): string[] => {
  const cells: string[] = []
  let cur = ''
  let tick = false
  const t = line.trim()
  for (let i = 0; i < t.length; i++) {
    const c = t[i]!
    if (c === '\\' && t[i + 1] === '|') {
      cur += '|'
      i++
    } else if (c === '`') {
      tick = !tick
      cur += c
    } else if (c === '|' && !tick) {
      cells.push(cur)
      cur = ''
    } else cur += c
  }
  cells.push(cur)
  if (t.startsWith('|')) cells.shift()
  if (cells.length && t.endsWith('|') && !t.endsWith('\\|')) cells.pop()
  return cells.map((c) => c.trim())
}

/**
 * A table starting at lines[at]: a header row with a pipe, then a separator row
 * (`---`, `:--`, `--:`, `:-:`) of the same width, then body rows up to a blank
 * line or a line with no pipe. Anything else is not a table (null) and stays text.
 */
export const parseTable = (lines: string[], at: number): { table: Table; end: number } | null => {
  const head = lines[at]
  const sep = lines[at + 1]
  if (head === undefined || sep === undefined || !head.includes('|') || !sep.includes('|')) return null
  const header = splitRow(head)
  const marks = splitRow(sep)
  if (marks.length !== header.length || !marks.every((m) => /^:?-+:?$/.test(m))) return null
  const align = marks.map((m): TableAlign => {
    const l = m.startsWith(':')
    const r = m.endsWith(':')
    return l && r ? 'center' : r ? 'right' : l ? 'left' : null
  })
  const rows: string[][] = []
  let end = at + 2
  while (end < lines.length && lines[end]!.trim() && lines[end]!.includes('|')) {
    const cells = splitRow(lines[end]!)
    rows.push(header.map((_, i) => cells[i] ?? ''))
    end++
  }
  return { table: { header, align, rows }, end }
}

const alignClass = (a: TableAlign) =>
  a === 'right' ? 'text-right' : a === 'center' ? 'text-center' : 'text-left'

/** A pipe table: scrolls sideways on a phone, hairline rules, no shadow. */
const MdTable = ({ table, k }: { table: Table; k: string }) => (
  <div className="my-2 overflow-x-auto" data-testid="md-table">
    <table className="border-collapse text-[0.92em] leading-snug">
      <thead>
        <tr>
          {table.header.map((h, i) => (
            <th
              key={i}
              className={`${alignClass(table.align[i] ?? null)} font-semibold px-2.5 py-1.5 border-b border-line-strong align-bottom`}
            >
              {inline(h, `${k}-h${i}`)}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {table.rows.map((r, ri) => (
          <tr key={ri}>
            {r.map((c, i) => (
              <td
                key={i}
                className={`${alignClass(table.align[i] ?? null)} px-2.5 py-1.5 border-b border-line align-top`}
              >
                {inline(c, `${k}-${ri}-${i}`)}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
)

/** Text and fenced code in order; a fence that never closes is left as text. */
export const splitFences = (
  text: string,
): ({ text: string; code?: undefined } | { code: string; lang: string })[] => {
  const out: ({ text: string; code?: undefined } | { code: string; lang: string })[] = []
  let last = 0
  for (const m of text.matchAll(/```([^\n`]*)\n?([\s\S]*?)```/g)) {
    if (m.index > last) out.push({ text: text.slice(last, m.index) })
    out.push({ code: m[2]!, lang: m[1]!.trim().toLowerCase() })
    last = m.index + m[0].length
  }
  if (last < text.length || !out.length) out.push({ text: text.slice(last) })
  return out
}

/** Render an agent's markdown-ish text. */
export const MdLite = ({
  text,
  className,
  testId,
}: {
  text: string
  className?: string
  testId?: string
}) => {
  const blocks: ReactNode[] = []
  // split on fences: with a pair of them, the odd pieces are code
  const pieces = splitFences(text)
  pieces.forEach((fence, n) => {
    if (fence.code !== undefined) {
      const body = fence.code.replace(/\n$/, '')
      blocks.push(
        fence.lang === 'mermaid' ? (
          <MermaidDiagram key={`c${n}`} source={body} className="my-2" />
        ) : (
          <CodeBlock key={`c${n}`} code={body} lang={fence.lang} />
        ),
      )
      return
    }
    const piece = fence.text
    const lines = piece.split('\n')
    let list: { ordered: boolean; items: string[] } | null = null
    let para: string[] = []
    const flushPara = (k: string) => {
      if (para.length)
        blocks.push(
          <p key={k} className="my-1.5">
            {inline(para.join('\n'), k)}
          </p>,
        )
      para = []
    }
    const flushList = (k: string) => {
      if (!list) return
      const items = list.items.map((it, i) => <li key={i}>{inline(it, `${k}-${i}`)}</li>)
      blocks.push(
        list.ordered ? (
          <ol key={k} className="list-decimal pl-5 my-1.5 space-y-0.5">
            {items}
          </ol>
        ) : (
          <ul key={k} className="list-disc pl-5 my-1.5 space-y-0.5">
            {items}
          </ul>
        ),
      )
      list = null
    }
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i]!
      const k = `${n}-${i}`
      const t = parseTable(lines, i)
      if (t) {
        flushPara(`p${k}`)
        flushList(`l${k}`)
        blocks.push(<MdTable key={`t${k}`} table={t.table} k={`t${k}`} />)
        i = t.end - 1
        continue
      }
      const bullet = /^\s*[-*]\s+(.*)$/.exec(line)
      const numbered = /^\s*\d+[.)]\s+(.*)$/.exec(line)
      const heading = /^#{1,6}\s+(.*)$/.exec(line)
      if (bullet || numbered) {
        flushPara(`p${k}`)
        const ordered = !!numbered
        if (list && list.ordered !== ordered) flushList(`l${k}`)
        if (!list) list = { ordered, items: [] }
        list.items.push((bullet ?? numbered)![1]!)
        continue
      }
      flushList(`l${k}`)
      if (heading) {
        flushPara(`p${k}`)
        blocks.push(
          <p key={`h${k}`} className="font-semibold mt-3 mb-1">
            {inline(heading[1]!, `h${k}`)}
          </p>,
        )
        continue
      }
      if (!line.trim()) {
        flushPara(`p${k}`)
        continue
      }
      para.push(line)
    }
    flushPara(`p${n}-end`)
    flushList(`l${n}-end`)
  })
  return (
    <div className={className} data-testid={testId}>
      {blocks}
    </div>
  )
}
